import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguages } from "@/hooks/useLanguages";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANG } from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

interface ConversationStage {
  id: string;
  name: string;
  description: string;
  stage_type: "review" | "checkin" | "objective_setting" | "skills_assessment";
  order_index: number;
  timing_weeks: number;
  is_optional: boolean;
  template_id: string;
}

interface ConversationStageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  stage: ConversationStage | null;
  onSuccess: () => void;
}

export const ConversationStageDialog = ({
  isOpen,
  onClose,
  stage,
  onSuccess,
}: ConversationStageDialogProps) => {
  const { i18n } = useTranslation();
  const { data: languages } = useLanguages();
  const [activeLang, setActiveLang] = useState<string>(i18n.language || DEFAULT_LANG);
  const [nameI18n, setNameI18n] = useState<Record<string, string>>({});
  const [descI18n, setDescI18n] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    stage_type: "review" | "checkin" | "objective_setting" | "skills_assessment";
    order_index: number;
    timing_weeks: number;
    is_optional: boolean;
    template_id: string;
  }>({
    name: "",
    description: "",
    stage_type: "review",
    order_index: 1,
    timing_weeks: 1,
    is_optional: false,
    template_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch templates for dropdown
  const { data: templates } = useQuery({
    queryKey: ['conversation-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_templates')
        .select('id, name')
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (stage) {
      setFormData({
        name: stage.name || "",
        description: stage.description || "",
        stage_type: stage.stage_type || "review",
        order_index: stage.order_index || 1,
        timing_weeks: stage.timing_weeks || 1,
        is_optional: stage.is_optional || false,
        template_id: stage.template_id || "",
      });
      const s: any = stage as any;
      setNameI18n((s.name_i18n as any) || {});
      setDescI18n((s.description_i18n as any) || {});
    } else {
      setFormData({
        name: "",
        description: "",
        stage_type: "review",
        order_index: 1,
        timing_weeks: 1,
        is_optional: false,
        template_id: "",
      });
      setNameI18n({});
      setDescI18n({});
    }
  }, [stage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Stage name is required");
      return;
    }

    if (!formData.template_id) {
      toast.error("Template selection is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData: any = {
        ...formData,
        name_i18n: nameI18n,
        description_i18n: descI18n,
      };

      if (stage) {
        // Update existing stage
        const { error } = await supabase
          .from('conversation_stages')
          .update(submitData)
          .eq('id', stage.id);

        if (error) throw error;
        toast.success("Stage updated successfully");
      } else {
        // Create new stage
        const { error } = await supabase
          .from('conversation_stages')
          .insert([submitData]);

        if (error) throw error;
        toast.success("Stage created successfully");
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving stage:', error);
      toast.error("Failed to save stage");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {stage ? "Edit Stage" : "Create New Stage"}
          </DialogTitle>
          <DialogDescription>
            {stage
              ? "Update the conversation stage details below."
              : "Create a new stage within a conversation template."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Stage Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Kickoff Meeting, Mid-Year Review"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what happens during this stage..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="template_id">Conversation Template</Label>
            <Select
              value={formData.template_id}
              onValueChange={(value) => setFormData({ ...formData, template_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                {templates?.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="stage_type">Stage Type</Label>
              <Select
                value={formData.stage_type}
                onValueChange={(value: "review" | "checkin" | "objective_setting" | "skills_assessment") => setFormData({ ...formData, stage_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="checkin">Check-in</SelectItem>
                  <SelectItem value="objective_setting">Objective Setting</SelectItem>
                  <SelectItem value="skills_assessment">Skills Assessment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="order_index">Order</Label>
              <Input
                id="order_index"
                type="number"
                min="1"
                value={formData.order_index}
                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 1 })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="timing_weeks">Timing (Weeks)</Label>
              <Input
                id="timing_weeks"
                type="number"
                min="1"
                value={formData.timing_weeks}
                onChange={(e) => setFormData({ ...formData, timing_weeks: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_optional"
              checked={formData.is_optional}
              onCheckedChange={(checked) => setFormData({ ...formData, is_optional: checked as boolean })}
            />
            <Label htmlFor="is_optional">This stage is optional</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : stage ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};